import {
  ColumnDef,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type ColumnFiltersState,
  type PaginationState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Table as TanTable } from "@tanstack/react-table";

import * as React from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { DataTableToolbar } from "./DataTableToolbar";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils";
import { LoaderCircle, Inbox, Search } from "lucide-react";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import Pagination from "../ui/pagination";
import { Skeleton } from "../ui/skeleton";

/* ──────────────── Types ──────────────── */

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  facets?: any[];
  isLoading?: boolean;
  isFetching?: boolean;
  defaultColumnVisibility?: VisibilityState;
  onSearch?: (value: string) => void;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setSorting?: React.Dispatch<React.SetStateAction<SortingState>>;
  sorting?: SortingState;
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>;
  pageIndex?: number;
  pageSize?: number;
  pageCount?: number;
  title?: string;
  className?: string;
  Action?: any;
  onExport?: () => void;
  hidePagination?: boolean;
  enableExport?: boolean;
  showSearch?: boolean;
  enableClientFiltering?: boolean;
  bulkDelete?: any;
  bulkActivate?: any;
  bulkVerify?: any;
  bulkApprove?: any;
  bulkReject?: any;
  refetch?: () => void;
  emptyMessage?: string;
  emptyDescription?: string;
  loadingRows?: number;
  stickyHeader?: boolean;
}

/* ──────────────── Loading Skeleton ──────────────── */

function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <TableRow key={`skeleton-${rowIdx}`} className="hover:bg-transparent">
          {Array.from({ length: columns }).map((_, colIdx) => (
            <TableCell key={`skeleton-${rowIdx}-${colIdx}`} className="px-4 py-3">
              <Skeleton className={cn(
                "h-4 rounded",
                colIdx === 0 ? "w-20" : colIdx === 1 ? "w-32" : "w-24"
              )} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

/* ──────────────── Empty State ──────────────── */

function EmptyState({ 
  message = "No results found", 
  description = "Try adjusting your search or filters",
  colSpan 
}: { 
  message?: string; 
  description?: string; 
  colSpan: number;
}) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-64 text-center">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <Inbox className="h-6 w-6 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{message}</p>
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

/* ──────────────── DataTable Component ──────────────── */

function DataTable<TData, TValue>({
  columns,
  data,
  facets = [],
  defaultColumnVisibility = {},
  isLoading = false,
  isFetching = false,
  onSearch,
  columnFilters: externalColumnFilters,
  setColumnFilters: externalSetColumnFilters,
  setSorting,
  sorting = [],
  setPagination,
  pageIndex = 0,
  pageSize = 10,
  pageCount = 1,
  className,
  onExport,
  Action,
  hidePagination = false,
  enableExport = true,
  showSearch = true,
  enableClientFiltering = false,
  bulkDelete,
  bulkActivate,
  bulkVerify,
  bulkApprove,
  bulkReject,
  refetch,
  emptyMessage,
  emptyDescription,
  loadingRows = 8,
  stickyHeader = true,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(defaultColumnVisibility);

  // Internal column filters state for client-side filtering mode
  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);

  // Use external state for server-side mode, internal state for client-side mode
  const columnFilters = enableClientFiltering ? internalColumnFilters : (externalColumnFilters || []);
  const setColumnFilters = enableClientFiltering ? setInternalColumnFilters : externalSetColumnFilters;

  // Reset page when filters change in client-side mode
  React.useEffect(() => {
    if (enableClientFiltering && setPagination) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [internalColumnFilters]);

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: !enableClientFiltering,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination,
    },
    enableRowSelection: true,
    pageCount: pageCount,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const rows = table.getRowModel().rows.filter((e) => !e.original["meta"]?.isFooter);
  const hasData = rows.length > 0;

  return (
    <div className="w-full">
      <Card
        className={cn(
          "shadow-none mt-3 sm:mt-0 relative rounded-md h-full border border-gray-200",
          className
        )}
      >
        {/* Progress bar for background fetching */}
        {isFetching && !isLoading && (
          <div className="h-[3px] opacity-70 absolute top-0 rounded-t-md bg-[#0A9F43]/20 w-full overflow-hidden z-20">
            <div className="h-full bg-[#0A9F43] animate-pulse" style={{ width: "60%" }} />
          </div>
        )}

        {/* Toolbar with search, filters, and actions */}
        <DataTableToolbar
          Action={Action}
          onSearch={onSearch}
          facets={facets}
          table={table}
          onExport={onExport}
          isLoading={isLoading}
          showSearch={showSearch}
          bulkDelete={bulkDelete}
          bulkActivate={bulkActivate}
          bulkVerify={bulkVerify}
          bulkApprove={bulkApprove}
          bulkReject={bulkReject}
          enableExport={enableExport}
          refetch={refetch}
        />

        {/* Table with horizontal scroll */}
        <ScrollArea className="w-full max-w-full overflow-x-auto">
          <Table id="dataTable" className="overflow-x-auto">
            {/* Brand green header */}
            <TableHeader className={cn(stickyHeader && "sticky top-0 z-10")}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow 
                  key={headerGroup.id} 
                  className="border-b-0 hover:bg-[#0A9F43]"
                >
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      className={cn(
                        "!bg-[#0A9F43] border-b border-[#0A9F43] border-r border-r-[#3ddb7a] last:border-r-0",
                        "text-white font-semibold h-11 text-[13px]",
                        "whitespace-nowrap px-4",
                        "last:text-right last:justify-end last:flex last:items-center"
                      )}
                      key={header.id}
                      colSpan={header.colSpan}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {isLoading ? (
                /* Loading skeleton */
                <TableSkeleton rows={loadingRows} columns={columns.length} />
              ) : hasData ? (
                /* Data rows */
                rows.map((row, rowIdx) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "transition-colors hover:bg-gray-50/80",
                      rowIdx % 2 === 1 && "bg-gray-50/40",
                      row.original["meta"]?.isFooter && "border-t-2 border-gray-200"
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        className={cn(
                          "last:text-right h-full last:justify-end last:flex last:items-center",
                          "!text-slate-600 font-normal",
                          "border-slate-100 border-r border-r-slate-200 last:border-r-0",
                          "px-4 py-3",
                          row.original["meta"]?.isFooter &&
                            "font-semibold border-t bg-primary/10 truncate !py-1.5 h-8 !text-primary text-[12.5px]"
                        )}
                        key={cell.id}
                      >
                        {row.original["meta"]?.isFooter &&
                        (cell.column.id === "select" ||
                          cell.column.id === "actions") ? null : (
                          <div className="py-0.5">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                /* Empty state */
                <EmptyState 
                  message={emptyMessage} 
                  description={emptyDescription}
                  colSpan={columns.length} 
                />
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Pagination */}
        {hidePagination ? null : (
          <div className="py-[7px] px-2 border-t border-gray-100 bg-white rounded-b-md">
            <DataTablePagination pageIndex={pageIndex} table={table} />
          </div>
        )}
      </Card>
    </div>
  );
}

export default DataTable;

/* ──────────────── Pagination Component ──────────────── */

interface DataTablePaginationProps<TData> {
  table: TanTable<TData>;
  pageIndex: number;
}

function DataTablePagination<TData>({
  table,
  pageIndex,
}: DataTablePaginationProps<TData>) {
  return (
    <div className="flex items-center gap-4 justify-between px-2">
      <div className="flex items-center gap-3">
        {table.getFilteredSelectedRowModel().rows.length > 0 && (
          <div className="flex-1 text-slate-500 text-sm">
            {table.getFilteredSelectedRowModel().rows.length} of{" "}
            {table.getFilteredRowModel().rows.length} row(s) selected
          </div>
        )}
        <div className="flex items-center space-x-2">
          <p className="text-[13px] text-slate-500 font-medium">
            Rows per page
          </p>
          <Select
            value={table.getState().pagination.pageSize >= 999999 ? "all" : `${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              if (value === "all") {
                table.setPageSize(999999);
              } else {
                table.setPageSize(Number(value));
              }
            }}
          >
            <SelectTrigger className="h-[30px] rounded-md bg-white w-[70px] border-gray-200">
              <SelectValue placeholder={table.getState().pagination.pageSize >= 999999 ? "All" : table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top" className="bg-white border-gray-200">
              {[10, 20, 30, 50, 100, -1].map((pageSize) => (
                <SelectItem key={pageSize} value={pageSize === -1 ? "all" : `${pageSize}`}>
                  {pageSize === -1 ? "All" : pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Pagination
        currentPage={pageIndex + 1}
        totalPages={table.getPageCount()}
        onPageChange={(e) => {
          table.setPageIndex(e - 1);
        }}
      />
    </div>
  );
}
